import React, { useCallback, useMemo, useState } from 'react';

// MUI
import { useTheme } from '@mui/material/styles';
import Paper from '@mui/material/Paper';

// Components
import SectionComponent from './Section';

// Utils
import { SECTION_ORDER } from './utils';
import ThemeProvider from '../../utils/theme';
import useCsrfToken from '../../utils/hooks/csfrToken';
import * as urls from '../../utils/urls';

// Styles
import styles from './style.module.scss';

// Types
import { OnboardingContextType, OnboardingData, Section } from './types';

// Context
import { OnboardingProvider } from './context';

// Svgs
import Star1 from '../../components/Svgs/Star1';
import Star2 from '../../components/Svgs/Star2';
import LoadingBackdrop from '../../components/LoadingBackdrop';


const Background = () => {
  const isDarkMode = useTheme().palette.mode === 'dark';
  return (
    <div className={styles.background}>
      {/* Overide the star1 gradient color */}
      <svg>
        <defs>
          <radialGradient id="paint0_radial_890_24583" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(569 434) rotate(-90) scale(614)">
            <stop stopColor="white" stopOpacity="0" />
            <stop offset="0.814095" stopColor="#8104FE" />
          </radialGradient>

          <radialGradient id="paint0_radial_1227_60530" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(704.001 434) rotate(-90) scale(857.999)">
            <stop stopColor="white" stopOpacity="0" />
            <stop offset="0.814095" stopColor="#8104FE" />
          </radialGradient>
        </defs>
      </svg>

      <Star1 className={styles.star1} opacity={isDarkMode ? 0.04 : 0.02} />
      <Star2 className={styles.star2} opacity={isDarkMode ? 0.02 : 0.04} />
    </div>
  )
}

type OnboardingProps = unknown;

const Onboarding: React.FC<OnboardingProps> = () => {
  const csrfToken = useCsrfToken();

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [currentSection, setCurrentSection] = useState<Section>(SECTION_ORDER[0]);
  const [showBackground, setShowBackground] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({} as OnboardingData);

  // Methods
  const handleNextSection = useCallback(() => {
    const currentIndex = SECTION_ORDER.indexOf(currentSection);
    const nextIndex = currentIndex + 1;

    if (nextIndex < SECTION_ORDER.length) {
      setCurrentSection(SECTION_ORDER[nextIndex]);
    }
  }, [currentSection]);

  const handlePreviousSection = useCallback(() => {
    const currentIndex = SECTION_ORDER.indexOf(currentSection);
    const previousIndex = currentIndex - 1;

    if (previousIndex >= 0) {
      setCurrentSection(SECTION_ORDER[previousIndex]);
    }
  }, [currentSection]);

  const handleFinishOnboarding = useCallback(async () => {
    setIsLoading(true);
    try {
      // TODO: Keith Check if this is correct and if the data is correct
      const options = {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ onboarding_data: onboardingData }),
      };

      const response = await fetch(urls.onboardingUserUrl(), options);
      if (response.ok) {
        // Redirect to home page
        Turbo.visit('/');
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [onboardingData, csrfToken]);

  // Context
  const contextValue = useMemo<OnboardingContextType>(() => ({
    currentSection,
    onNextSection: handleNextSection,
    onPreviousSection: handlePreviousSection,
    setShowBackground,
    setOnboardingData,
    onFinishOnboarding: handleFinishOnboarding,
  }), [currentSection, handleNextSection, handlePreviousSection, setShowBackground, setOnboardingData, handleFinishOnboarding]);

  const isLastSection = useMemo(() => {
    return currentSection === SECTION_ORDER[SECTION_ORDER.length - 1];
  }, [currentSection]);

  // Render
  return (
    <ThemeProvider>
      <OnboardingProvider value={contextValue}>
        <Paper className={styles.paper}>
          {isLoading && <LoadingBackdrop />}

          {showBackground && <Background />}

          <SectionComponent
            section={currentSection}
            isLastSection={isLastSection}
            onNextSection={handleNextSection}
          />
        </Paper>
      </OnboardingProvider>
    </ThemeProvider>
  )
};

export default Onboarding;
